/** Durée max alignée sur le backend (session active ou envoi vidéo récent). */
const SESSION_RECORDING_MAX_AGE_MS = 20 * 60 * 1000;

export const SESSION_DISPLAY_LABELS = {
  new: "Nouveau",
  recording: "En cours",
  consumed: "Consommé",
  finished: "Terminé",
};

export const SESSION_DISPLAY_BADGE = {
  new: "bg-violet-500/20 text-violet-300 border-0",
  recording: "bg-md-cyan/20 text-md-cyan border-0",
  consumed: "bg-amber-500/20 text-amber-300 border-0",
  finished: "bg-emerald-500/20 text-emerald-400 border-0",
};

const VALID_DISPLAY = new Set(["new", "recording", "consumed", "finished"]);

function sessionHasVideo(session) {
  return Boolean(session?.video_composite_storage_path || session?.video_storage_path);
}

function isSessionInProgress(session) {
  if (!session) return false;
  const now = Date.now();
  if (session.status === "active") {
    const started = new Date(session.started_at).getTime();
    return Number.isFinite(started) && now - started <= SESSION_RECORDING_MAX_AGE_MS;
  }
  if (session.status === "completed" && !sessionHasVideo(session)) {
    const ended = new Date(session.ended_at || session.started_at).getTime();
    return Number.isFinite(ended) && now - ended <= SESSION_RECORDING_MAX_AGE_MS;
  }
  return false;
}

function normalizeLegacyDisplayStatus(value, session) {
  if (value === "completed") return sessionHasVideo(session) ? "finished" : "consumed";
  if (value === "abandoned") return "consumed";
  return value;
}

function computeDisplayStatus(session) {
  if (session?.is_used === false) return "new";
  if (sessionHasVideo(session)) return "finished";
  if (session?.status === "aborted") return "consumed";
  if (isSessionInProgress(session)) return "recording";
  if (session?.is_used === true || session?.status) return "consumed";
  return "new";
}

export function getSessionDisplayStatus(session) {
  const fromApi = session?.display_status;
  if (fromApi) {
    const normalized = normalizeLegacyDisplayStatus(fromApi, session);
    if (VALID_DISPLAY.has(normalized)) return normalized;
  }
  return computeDisplayStatus(session);
}

export function canResetSessionCode(session) {
  const ds = getSessionDisplayStatus(session);
  if (ds === "finished" || sessionHasVideo(session)) return false;
  if (ds === "new") return false;
  return ds === "consumed" || ds === "recording";
}

export function hasPlayedSession(session) {
  if ((session?.results || []).length > 0) return true;
  return (session?.current_question_index ?? 0) > 0;
}

export function shouldShowSessionStats(session) {
  const ds = getSessionDisplayStatus(session);
  if (ds === "new") return false;
  if (ds === "finished") return true;
  return hasPlayedSession(session);
}

export function formatSessionLostLabel(session) {
  if (!shouldShowSessionStats(session)) return "—";
  const results = session?.results || [];
  if (!results.length) return "—";
  const timedOut = results.filter((r) => r.timed_out || !r.answered).length;
  if (timedOut <= 0) return "0 timeout";
  return `${timedOut} timeout${timedOut > 1 ? "s" : ""}`;
}

export function formatSessionAnsweredLabel(session) {
  if (!shouldShowSessionStats(session)) return "—";
  const results = session?.results || [];
  if (!results.length) return "—";
  const answered = results.filter((r) => r.answered && !r.timed_out).length;
  return `${answered}/${results.length}`;
}

export function getSessionResetCount(session) {
  return Number(session?.reset_count ?? 0);
}

export function isNewRegistrationRow(session) {
  return getSessionDisplayStatus(session) === "new";
}
